import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { fetchBilling } from '@/store/slices/billingSlice';
import type { BillingKind, BillingStatus, Capability, DailyMedia, OwnableTier } from '@/services/billingApi';

export interface Entitlements {
  /** True once a billing snapshot has loaded for a real user. */
  loaded: boolean;
  status: BillingStatus | null;
  kind: BillingKind | 'guest';
  paywallEnabled: boolean;
  /**
   * Whether the user may use a capability. IMPORTANT: returns true while the
   * paywall is OFF (the dark-ship default) and while billing is still loading,
   * so gated UI never flickers/hides until enforcement is actually on. Only when
   * the paywall is enabled AND a snapshot has loaded does it gate by capability.
   */
  can: (cap: Capability) => boolean;
  ownedTier: OwnableTier | null;
  tier3Active: boolean;
  dailyMedia: DailyMedia | null;
  isRecruiter: boolean;
}

/**
 * The single hook every gated surface consumes. Reads the billing snapshot from
 * the store (fetching it once for a logged-in user) and exposes a `can()`
 * capability check plus the raw status. Pair with the <Gate> component for
 * conditional rendering.
 */
export function useEntitlements(opts: { autofetch?: boolean } = {}): Entitlements {
  const dispatch = useAppDispatch();
  const { user, status: authStatus } = useAuth();
  const data = useAppSelector((s) => s.billing.data);
  const loadStatus = useAppSelector((s) => s.billing.status);

  const isRealUser = authStatus === 'authenticated' && user != null && !user.guest;

  useEffect(() => {
    if (opts.autofetch === false) return;
    if (isRealUser && loadStatus === 'idle') void dispatch(fetchBilling());
  }, [opts.autofetch, isRealUser, loadStatus, dispatch]);

  const paywallEnabled = data?.paywallEnabled ?? false;

  const can = useCallback(
    (cap: Capability): boolean => {
      if (!paywallEnabled) return true; // dark-shipped — show everything
      if (!data) return true; // not loaded yet — don't hide prematurely
      return data.capabilities.includes(cap);
    },
    [paywallEnabled, data],
  );

  return useMemo<Entitlements>(
    () => ({
      loaded: data != null,
      status: data,
      kind: isRealUser ? data?.kind ?? 'none' : 'guest',
      paywallEnabled,
      can,
      ownedTier: data?.ownedTier ?? null,
      tier3Active: data?.tier3Active ?? false,
      dailyMedia: data?.dailyMedia ?? null,
      isRecruiter: data?.isRecruiter ?? false,
    }),
    [data, isRealUser, paywallEnabled, can],
  );
}
